// Which drawer a control lives in, and what stays visible when its help text
// moves to hover (founder go 2026-09-18: "make it cleaner and more premium
// looking... instead of the writing, just put it into a hover over").
//
// WHY THIS FILE EXISTS. The page carried 159 controls and 3,574 words of
// standing prose across twelve stacked cards, and every section heading was a
// bold paragraph one weight step from the labels beneath it, so nothing read
// as a section. Grouping used to follow WHERE A FLAG IS ENFORCED (the old
// SERVER_FLAG_DEFS was literally "the app never reads these"), which is a fact
// about the server, not a question an operator ever asks. These sections
// follow what the operator came to change.
//
// 🔴 THE RULE FOR WHAT MAY MOVE TO HOVER. If a string says what flipping the
// switch does to real people right now, or carries a "Keep OFF" instruction,
// a pre-submission instruction or a named past incident, it STAYS VISIBLE.
// Mechanism, history, and client-version trivia go to hover. Without a written
// rule this decision gets made thirty-seven times by feel.

// Flags whose flip reaches real members within about a minute. Drives the red
// pill, the amber strip on change, and the count in the save bar. This is a
// DATA field on purpose: a thirty-eighth flag cannot silently join the list
// unmarked, the way it would if this were eyeballed at render time.
export const REACHES_MEMBERS = new Set([
  "truth_gate_v1",
  // Removes members' messages and pushes them within a second of sending.
  "offapp_guard_v1",
  // The server's message check (2026-09-29): removes messages and holds
  // every chat notification until each message is checked.
  "chat_server_check_v1",
  "daily_question",
  "meetup_v1",
  "intro_card_v1",
  "penpal_beyond_city",
  "curiouzz_promise_v1",
  "daily_cadence_v1",
  // The quiet stage (founder 2026-11-04): both change what the empty stage
  // says to members.
  "age_curiosity_v1",
  "stage_their_settings_line_v1",
  "end_friction_v1",
  "meetup_free_unlock",
  "meetup_proof_v1",
  "count_gatekeeper_only",
  "chat_message_push",
  "reveal_hold_v1",
  "quiet_rotation_v1",
  // Live updates (2026-09-26): blocking holds a 2.3 phone's connection until
  // its member is seen from the same address; requiring the login cuts live
  // updates to every phone older than 2.4. The alert-only switch changes
  // nothing for members, so it is not on this list.
  "socket_guard_enforce_v1",
  "socket_auth_required",
  // Take a break (2026-09-27): ON puts the pause row and sheet in front of
  // every 2.4 member. The pause itself never depends on it.
  "intro_pause_v1",
  // The closing notice (2026-09-29): ON pushes both members at 6 PM when an
  // introduction neither of them started closes.
  "intro_closed_notice_v1",
  // The chat quiet window (2026-09-29): ON changes when and how often every
  // member's chat notifications arrive.
  "chat_quiet_window_v1",
  // The photo wall reminder for Ottawa's early stoppers (2026-10-01): ON
  // reminds them at the next hourly run.
  "photo_nudge_pre_habits_v1",
  // Kept, Together (2026-10-02): ON gives every 2.4 pair at the message
  // limit its card the next time either of them opens the conversation.
  "kept_together_v1",
  // Its emergency release (review 2026-10-02): ON lets every member at a
  // waiting card keep writing.
  "kept_release_pause_v1",
  // The Club value round (2026-10-07): each one changes what a member sees.
  "read_in_club_v1",
  "daily_question_together_v1",
  "daily_question_ask_v1",
  "daily_question_together_stays_v1",
  // Add a line (2026-10-08): ON shows the row under every 2.5 member's Ice
  // Breaker and Wild Card answers, and the lines the other person wrote.
  "vault_line_v1",
  // Guess her answer (2026-10-19): ON gives every member on 2.6 or newer one
  // Vault question a day under a revealed connection they are waiting on.
  // 2.5 and older never ask for it.
  "guess_v1",
  // Its room card (2026-10-19, "Do 1 only"): held, dark until ON as well,
  // and no build draws it yet.
  "guess_room_v1",
  // Introductions never bother a member in a conversation, and the Promise
  // fixes (2026-10-08): each one changes what members are told, or when.
  "quiet_intro_in_conversation_v1",
  "quiet_intro_release_push_v1",
  "quiet_card_close_notice_v1",
  "promise_prompt_unblocked_v1",
  "promise_release_same_evening_v1",
  "promise_close_quiet_side_push_v1",
  // The new-member minimum (2026-10-08): ON changes who a new member is
  // introduced to, from the next deal.
  "intro_min_score_new_members_v1",
  // A "ready" push must still be true when it is read (2026-10-09): each one
  // changes when members are told, when they are introduced, or what they see.
  "intro_push_stagger_v1",
  "unseen_close_no_wait_v1",
  "unseen_close_line_v1",
  "intro_push_withdraw_v1",
  // Change my photos (2026-10-13): ON shows the button to every 2.5 member
  // whose face check during sign-up did not recognise them in their photos.
  "face_check_change_photos_v1",
  // The 2.6 onboarding (2026-10-15): ON changes the sign-up screens every
  // 2.6 member still signing up meets, from their next screen.
  "onboarding_v26",
  // The face check must see eyes (2026-10-14): ON fails face checks that
  // passed before (a selfie or photos in sunglasses).
  "face_check_eyes_visible_v1",
  "face_check_clear_photo_v1",
  // The city map on the 2.6 place card (2026-10-19): OFF swaps the map for
  // the drawn grid on the "Where are you based?" card, within a minute.
  "place_map_v1",
  // The 2.6 emailed code (2026-10-19): changes how everyone on 2.6 creates
  // an account and signs in, within a minute.
  "email_code_auth_v1",
  // The chat fixes (founder go 2026-10-21, "Go on 1-6"): each changes who is
  // signed out, told, reminded or introduced, from the next request or tick.
  "auth_forgive_v1",
  "removal_writer_v1",
  "offapp_precheck_v1",
  "intro_open_conversation_v1",
  "unread_reminder_v1",
  "reply_nudge_after_hello_v1",
]);

// One short clause under the label, visible at rest, on any row whose flip
// changes what a member sees or receives. Deliberately about consequence, not
// mechanism: the mechanism is what moved to hover.
export const CONSEQUENCE = {
  truth_gate_v1: "OFF sends every push unchecked, including ones that are no longer true",
  offapp_guard_v1: "ON removes messages that share usernames for other apps and tells the sender why, within seconds",
  chat_server_check_v1:
    "ON removes messages that break the chat rules and holds each chat notification until its message is checked, 5 seconds at most",
  daily_cadence_v1:
    "ON changes how every member receives introductions, immediately",
  end_friction_v1: "Changes the wait a member gets after ending a connection",
  reveal_hold_v1: "OFF returns every member to the ordinary wait after a reveal",
  age_curiosity_v1: "OFF hides the age offer on every empty stage, at once",
  stage_their_settings_line_v1:
    "ON adds the line to the Why it's quiet sheet for every member it is true for, at their next stage load",
  quiet_rotation_v1: "ON ends pairs automatically when a reveal offer expires",
  meetup_free_unlock:
    "OFF also removes free chat from pairs that already had it",
  meetup_proof_v1: "Changes whether a declared meetup has to be confirmed",
  count_gatekeeper_only: "Turning this ON lowers the member count members see",
  daily_question: "ON pushes the question card to real members immediately",
  meetup_v1: "Adds a booking chip to every revealed conversation",
  intro_card_v1: "OFF restores the classic Compatibility screen for everyone",
  penpal_beyond_city:
    "Keep OFF until the moderation runbook has handled real reports",
  chat_message_push: "Turns the message buzz on every phone on or off",
  curiouzz_promise_v1: "Closes quiet pairs nightly and tells both people",
  server_geocode_v1: "OFF is the kill switch for the location screen",
  pocket_gating: "OFF lets any member be introduced to any other",
  socket_guard_enforce_v1:
    "ON holds live updates on a phone without a valid login (2.3) until its member's own app is seen on the same network",
  socket_auth_required: "ON cuts live updates to every phone older than 2.4",
  city_pages_v1: "OFF makes every public city page and sitemap.xml a 404",
  intro_pause_v1:
    "ON lets members on 2.4 pause their own introductions from Settings; support can pause anyone either way",
  intro_closed_notice_v1:
    "ON tells both members at 6 PM when an introduction neither of them started closes",
  chat_quiet_window_v1:
    "ON holds further chat notifications about the same conversation for 10 minutes, then sends one",
  photo_nudge_pre_habits_v1:
    "ON reminds Ottawa members who stopped before the habits questions, at the next hourly run",
  kept_together_v1:
    "ON gives every 2.4 pair at the message limit a free card that lifts their limits once both answer",
  kept_release_pause_v1:
    "ON lets members at a waiting Kept, Together card keep writing without answering it",
  read_in_club_v1: "ON opens every Club member’s Curiouzz Read while their Club is live",
  daily_question_together_v1:
    "ON shows a revealed pair both their Daily Question answers in their conversation when either has the Club",
  daily_question_ask_v1: "ON lets Club members send a Daily Question for review; it runs only once you publish it",
  daily_question_together_stays_v1:
    "ON keeps a pair's two Daily Question answers in their conversation for the full two days, even after they have talked",
  vault_line_v1:
    "ON lets members add one short typed line under an Ice Breaker or Wild Card answer; the other person reads it only after answering",
  guess_v1:
    "ON gives members on 2.6 or newer one Vault question a day while they wait on a revealed, still-open connection: they guess that person's answer, then see it; 2.5 and older never show it",
  guess_room_v1:
    "ON adds the quiet-stage card, the city's answers in counts, for members with nobody to wait on (needs Guess her answer ON and a 2.6 build that draws it)",
  quiet_intro_in_conversation_v1:
    "ON stops every introduction push to a member who is in a live conversation; her card waits quietly and closes with no blame at 6 PM if unopened",
  quiet_intro_release_push_v1: "ON pushes her the waiting introduction when her conversation ends, in the daytime",
  quiet_card_close_notice_v1: "ON tells the other person when a quiet introduction closes at 6 PM",
  promise_prompt_unblocked_v1: "ON sends the Promise prompt even after another reminder or an email unsubscribe",
  promise_release_same_evening_v1: "ON gives members the Promise releases at 6 PM a new introduction that same evening",
  promise_close_quiet_side_push_v1: "ON pushes the member who went quiet when the Promise closes their introduction",
  // Review 2026-10-08: never "current members are unchanged". Their pairs
  // with each other follow the old rule, but their evenings can change.
  intro_min_score_new_members_v1:
    "ON: every new introduction with a new member (signed up on or after the start below, or not in a Live city at it) must show at least the minimum; some current members may then be introduced to someone else that evening",
  intro_push_stagger_v1:
    "ON tells one person of each new introduction first; the other person's push waits 10 minutes and goes only if it is still true",
  unseen_close_no_wait_v1:
    "ON lets someone whose introduction closed before they ever saw it be introduced again at once, with no 30-minute wait",
  unseen_close_line_v1:
    "ON tells someone on their empty stage that an introduction closed before they could open it, and that it was nothing they did",
  intro_push_withdraw_v1:
    "ON takes a stale \"Your next introduction is ready\" off 2.5 phones when the introduction closed before they saw it",
  face_check_change_photos_v1:
    "ON lets members on 2.5 whose face check during sign-up fails on their photos change those photos and try again, with no new account",
  onboarding_v26:
    "ON gives everyone signing up on 2.6 or newer the new onboarding in four chapters; someone part-way through goes on in it from their next unanswered step",
  face_check_eyes_visible_v1:
    "ON asks every member whose face check selfie shows sunglasses or a second face to take it again",
  face_check_clear_photo_v1:
    "ON stops a face check passing through a photo in sunglasses or with a tiny face where the member can change photos; on 2.4 sign-ups it rings the bell instead",
  place_map_v1:
    "ON shows people signing up on 2.6 or newer their city on a Google map in our colours; OFF gives them the drawn grid",
  email_code_auth_v1:
    "ON gives people on 2.6 or newer an emailed code instead of a password to create an account or sign in; OFF brings the password back",
  auth_forgive_v1:
    "ON keeps a member signed in once when their phone missed a sign-in renewal; OFF signs them out on every phone, as before",
  removal_writer_v1:
    "ON tells only the writer, by push, why their message was taken out; the other person is never buzzed about it",
  offapp_precheck_v1: "ON stops a 2.6 phone sending a message the guard would take out; only the writer sees why",
  intro_open_conversation_v1:
    "ON deals a new card beside an open, quiet conversation with no deadline, and says the conversation stays open",
  unread_reminder_v1: "ON sends one push when a message is still unread after 2 hours, daytime only",
  reply_nudge_after_hello_v1: "ON starts the reply nudge's day of silence at the first real message, not the reveal hello",
};

// Permanent, hard-coded, never derived and never behind hover. A derived
// summary gets refactored away; a literal line does not.
export const STANDING_WARNING = {
  truth_gate_v1: "Keep ON. It is how we never notify members of things that are not true.",
  daily_cadence_test_v1: "Turn this OFF before any store submission.",
  curiouzz_promise_v1:
    "The live ladder messages both members and closes pairs nightly at 6:00 PM.",
  socket_auth_required:
    "Turn ON only once every phone runs 2.4 or later (raise the minimum app version first); older versions will lose live updates.",
  intro_pause_v1: "Turn ON once 2.4 is live on both stores.",
  // Review 2026-09-29: the server stops at once, a 2.4 phone within a minute.
  chat_server_check_v1:
    "2.4 stops its own check only while this and the In-Chat Line are both ON. The server stops checking the moment this goes OFF; a 2.4 phone starts checking again within a minute. For no unchecked minute, turn the In-Chat Line OFF first, wait two minutes, then this.",
  kept_together_v1: "Seed the questions before turning this ON. OFF never closes a conversation that already opened.",
  kept_release_pause_v1: "Emergency only. Keep OFF unless the card is failing members.",
  // The wording of these two pushes was proposed 2026-10-08 and awaits the
  // founder's approval.
  quiet_card_close_notice_v1: "Keep OFF until the wording of this push is approved.",
  promise_close_quiet_side_push_v1: "Keep OFF until the wording of this push is approved.",
  // The chat fixes (2026-10-21): each waits on a preview or a test first.
  intro_open_conversation_v1: "Keep OFF until the preview is approved. Works only with Introductions Wait Quietly ON.",
  unread_reminder_v1: "Keep OFF until the test push and the test email are approved.",
  reply_nudge_after_hello_v1: "Keep OFF until the preview is approved.",
  offapp_precheck_v1: "Does nothing until a 2.6 build that asks is in the stores, and only with Stop Usernames for Other Apps ON.",
  // The new-member minimum (2026-10-08): the switch alone does nothing, and
  // who counts as new is the account's age and whether its city was Live at
  // the start, never its first introduction.
  intro_min_score_new_members_v1:
    "Set the start below first: without one this switch does nothing. New members are accounts created on or after the start, and members who were not in a Live city at the start (they stay new after their city opens). A member who signed up before the start and was already in a Live city then is current, even if never introduced (42 in Ottawa on 8 October).",
  // Told first (2026-10-09, review): the order to turn the two on, and what
  // changes at the 6 PM run, in plain sight.
  intro_push_stagger_v1:
    "Turn ON after No Wait After an Introduction Never Seen. At 6 PM the person told second hears 'Chosen with care. Your Vault is waiting.' without 'within a day to keep it'.",
  // The withdraw message (2026-10-09): a 2.5 build without the handler draws
  // it as "New Message".
  intro_push_withdraw_v1:
    "Turn ON only once the 2.5 build that removes the notice is live in both stores: an earlier 2.5 test build would show it as \"New Message\". Never sent to 2.3 or 2.4.",
  // The eyes rules (2026-10-14, review): what 2.4 says, and where the photo
  // rule stops a check, in plain sight.
  face_check_eyes_visible_v1:
    "On 2.4 (every store app today) a member asked for a new selfie sees 2.4's usual words, which say the selfie did not look enough like the photos: not true for this reason. 2.5 has the true words.",
  face_check_clear_photo_v1:
    "During sign-up it applies only on 2.5 and newer with Change My Photos After a Failed Face Check ON. On 2.4 sign-ups it does not stop the check: a pass only through a photo that is not clear rings the admin bell.",
};

// What a closed drawer's summary says about a standing-warning switch, and
// in which state. truth_gate_v1 is ON unless explicitly OFF, so its alarm is
// OFF. `missing`: what to say when the server's settings do not name the
// switch at all (an admin bundle ahead of its backend): never read as OFF.
export const SUMMARY_ALERT = {
  daily_cadence_test_v1: { when: true, text: "test accounts ON" },
  curiouzz_promise_v1: { when: true, text: "Promise LIVE" },
  truth_gate_v1: { when: false, text: "truth check OFF", missing: "truth check state not reported by the server" },
};

/** Pure: the drawer-summary alert for one switch, or null. */
export const summaryAlertFor = (name, flags) => {
  const a = SUMMARY_ALERT[name];
  if (!a) return null;
  const v = flags ? flags[name] : undefined;
  if (typeof v !== "boolean") return a.missing || null;
  return v === a.when ? a.text : null;
};

// Small chips that keep meaning visible after the prose moves to hover.
export const STATE_TAG = {
  server_geocode_v1: "2.3 and newer",
  vault_lockstep_v1: "2.2 clients",
  pair_profile_v1: "2.2 clients",
  low_density_ux: "Not shipped",
  cohort_drops: "Not shipped",
  events_density: "Not shipped",
  reveal_note_v1: "Held",
  reveal_hello_v1: "2.4 clients",
  pre_funnel_honesty: "Held",
  daily_cadence_test_v1: "Test only",
  answer_promise_dry_run: "Watches only",
  socket_guard_alert_v1: "Watches only",
  // Possible duplicate accounts (2026-10-13): lists pairs for staff, changes
  // nothing for any member.
  dupe_flag_v1: "Watches only",
  socket_auth_required: "2.4 and newer",
  intro_pause_v1: "2.4 and newer",
  kept_together_v1: "2.4 and newer",
  read_in_club_v1: "2.5 and newer",
  daily_question_together_v1: "2.5 and newer",
  daily_question_ask_v1: "2.5 and newer",
  daily_question_together_stays_v1: "2.5 and newer",
  vault_line_v1: "2.5 and newer",
  guess_v1: "2.6 and newer",
  guess_room_v1: "Held",
  intro_push_withdraw_v1: "2.5 and newer",
  face_check_change_photos_v1: "2.5 and newer",
  onboarding_v26: "2.6 and newer",
  place_map_v1: "2.6 and newer",
  email_code_auth_v1: "2.6 and newer",
  offapp_precheck_v1: "2.6 and newer",
};

// The eight drawers, in the order an operator meets them. `flags` are flag
// names in render order; `kind` marks the sections that render their own
// bespoke content rather than a list of switches.
//
// 🔴 COMPANIONS MUST STAY ADJACENT AND IN THE SAME SECTION. daily_cadence_v1
// and daily_cadence_test_v1 (one overrides the other), Daily Question and its
// Club early-access companion (the companion is ignored once the main one is
// ON), and meetup_free_unlock beside meetup_proof_v1. Splitting a pair across
// drawers means reading one switch without the other that changes its meaning.
export const SECTIONS = [
  {
    id: "doors",
    icon: "mdi:door-open",
    title: "Doors and Pockets",
    kind: "pockets",
    // Possible duplicate accounts (2026-10-13) sits with who comes in: it
    // only lists sign-ups that may be one person, for review.
    // City pages on www.curiouzz.com (2026-11-05) sit with the pockets they
    // are made from: public pages for search engines, nothing for members.
    flags: ["pocket_gating", "penpal_beyond_city", "count_gatekeeper_only", "dupe_flag_v1", "city_pages_v1"],
  },
  {
    id: "introductions",
    icon: "mdi:swap-horizontal",
    title: "Introductions",
    flags: [
      "intro_inventory_gate",
      "intent_pairing_v1",
      "reveal_hold_v1",
      // Guess her answer (2026-10-19), beside the hold: one Vault question a
      // day under the revealed card while the member waits on that person.
      // Reads the Vault, never writes to it, and changes no introduction.
      "guess_v1",
      "daily_cadence_v1",
      "daily_cadence_test_v1",
      // Introductions wait quietly during a conversation (2026-10-08).
      "quiet_intro_in_conversation_v1",
      // Companion (2026-10-21): acts only while the switch above is ON.
      "intro_open_conversation_v1",
      // No wait after an introduction you never saw (2026-10-09).
      "unseen_close_no_wait_v1",
      // The new-member minimum (2026-10-08). Its minimum and start render
      // right under it (AdminSettingLayer, newMemberMinimumRows).
      "intro_min_score_new_members_v1",
      "end_friction_v1",
      "quiet_rotation_v1",
      "re_pair_stale_v1",
      // The quiet stage (founder 2026-11-04, "Yes add both rows").
      "age_curiosity_v1",
      "stage_their_settings_line_v1",
    ],
  },
  {
    id: "chat",
    icon: "mdi:chat-outline",
    title: "Chat, Vault and Reveal",
    kind: "ladder",
    flags: [
      // Kept, Together (2026-10-02): what a 2.4 pair meets at the message
      // limit, first because it decides what the ladder's last rung does.
      "kept_together_v1",
      // Companion (2026-10-02): the release only means anything beside it.
      "kept_release_pause_v1",
      "meetup_free_unlock",
      "meetup_proof_v1",
      "vault_lockstep_v1",
      "vault_voice_v1",
      // Add a line (2026-10-08): the typed companion of Say more, on the
      // tiers Say more does not cover.
      "vault_line_v1",
      "pair_profile_v1",
      "reveal_thread_seed",
      // Companions: the line only means anything with the guard or the
      // server's message check on (2026-09-29), and explains both.
      "offapp_guard_v1",
      "chat_server_check_v1",
      "offapp_notice_line",
      // Companions (2026-10-21): who is told about a removal, and the 2.6
      // check before sending. Both act only with the guard above.
      "removal_writer_v1",
      "offapp_precheck_v1",
    ],
  },
  {
    id: "messages",
    icon: "mdi:bell-outline",
    title: "Messages and the Promise",
    flags: [
      "truth_gate_v1",
      "chat_message_push",
      // Companion (2026-09-29): the quiet window shapes the message buzz.
      "chat_quiet_window_v1",
      // A message never waits unseen (2026-10-21): after the message push,
      // the one reminder and the reply nudge's start.
      "unread_reminder_v1",
      "reply_nudge_after_hello_v1",
      "first_intro_notice",
      "next_intro_push",
      // A "ready" push must still be true when it is read (2026-10-09):
      // told first, the line when it closed unseen, and the stale notice
      // taken off 2.5 phones.
      "intro_push_stagger_v1",
      "unseen_close_line_v1",
      "intro_push_withdraw_v1",
      // The closing notice (2026-09-29), beside the push it folds into.
      "intro_closed_notice_v1",
      // Companions of quiet introductions (2026-10-08): nothing alone.
      "quiet_intro_release_push_v1",
      "quiet_card_close_notice_v1",
      "vault_idle_reminder",
      "vault_started_notice",
      "reveal_waiting_notice",
      "distance_widen_nudge",
      "photo_nudge_v1",
      // Companion (2026-10-01): adds to the reminder above, nothing alone.
      "photo_nudge_pre_habits_v1",
    ],
    subGroups: [
      {
        after: "photo_nudge_v1",
        label: "The Curiouzz Promise",
        flags: [
          "answer_promise_dry_run",
          "curiouzz_promise_v1",
          // The Promise fixes (2026-10-08): each does nothing while the live
          // ladder is OFF.
          "promise_prompt_unblocked_v1",
          "promise_release_same_evening_v1",
          "promise_close_quiet_side_push_v1",
        ],
      },
    ],
  },
  {
    id: "app",
    icon: "mdi:cellphone-cog",
    title: "The app members are running",
    kind: "app",
    flags: [
      "intro_card_v1",
      "daily_question",
      "daily_question_club_early",
      "meetup_v1",
      // Take a break (2026-09-27): what 2.4 shows, beside the other app doors.
      "intro_pause_v1",
      // The Club value round (2026-10-07): what 2.5 shows on the Club screen,
      // the You tab and in a revealed conversation.
      "read_in_club_v1",
      "daily_question_together_v1",
      "daily_question_ask_v1",
      "daily_question_together_stays_v1",
      // The 2.6 onboarding (2026-10-15): what 2.6 shows someone signing up.
      "onboarding_v26",
      // Change my photos (2026-10-13): what 2.5 shows on the sign-up face
      // check after a failure about the photos.
      "face_check_change_photos_v1",
      // Companions (2026-10-14): the face check must see eyes. The photo
      // rule is enforced during sign-up only with Change my photos ON.
      "face_check_eyes_visible_v1",
      "face_check_clear_photo_v1",
      "server_geocode_v1",
      // The city map on the 2.6 place card (2026-10-19), beside the location
      // screen's geocoder: both shape "Where are you based?".
      "place_map_v1",
      // The 2.6 emailed code (2026-10-19): how 2.6 creates an account and
      // signs in, the first screens someone signing up meets.
      "email_code_auth_v1",
      // Never signed out by a lost renewal (2026-10-21): how every phone
      // stays signed in, beside how 2.6 signs in.
      "auth_forgive_v1",
    ],
    // Companions (2026-09-26): the alert watches, blocking is the fix for
    // 2.3, and requiring the login comes once every phone runs 2.4. Here
    // beside the minimum version, which gates the last one.
    subGroups: [
      {
        label: "Live updates: who a phone is",
        flags: ["socket_guard_alert_v1", "socket_guard_enforce_v1", "socket_auth_required"],
      },
    ],
  },
  {
    id: "engine",
    icon: "mdi:tune-variant",
    title: "Engine and limits",
    kind: "engine",
    flags: [],
  },
  // Outside services Curiouzz signs in to (2026-10-19). Eventbrite first, for
  // Events design B: no switches, its card says what is connected.
  {
    id: "integrations",
    icon: "mdi:link-variant",
    title: "Integrations",
    kind: "integrations",
    flags: [],
  },
  {
    id: "held",
    icon: "mdi:archive-outline",
    title: "Held and not shipped",
    flags: [
      "low_density_ux",
      "cohort_drops",
      "events_density",
      "reveal_note_v1",
      "reveal_hello_v1",
      "pre_funnel_honesty",
      // Guess the room, held (founder 2026-10-19, "Do 1 only"): the quiet
      // stage already draws the Daily Question's city split, and no build
      // draws this card. Apart from Guess her answer on purpose: it does
      // nothing unless that one is ON (its help says so), while Guess her
      // answer means the same whatever this says.
      "guess_room_v1",
    ],
  },
];

// Sections that can reach live members open on a fresh session. Only the inert
// ones start closed.
//
// 🔴 DEFAULT-CLOSED IS THE DANGEROUS PART OF A DRAWER DESIGN. A console whose
// resting state shows zero switches means nobody can see what is currently ON
// without opening everything, which during an incident is slower than the page
// it replaced. localStorage remembers each operator's own preference from here.
export const DEFAULT_OPEN = new Set(["doors", "introductions", "chat", "messages", "app"]);

export const wordCount = (s) => String(s || "").trim().split(/\s+/).filter(Boolean).length;

// Help of 25 words or fewer fits a tooltip. Everything longer previews on
// hover and opens in place, because a 142-word tooltip (daily_cadence_v1)
// covers the switch it is explaining.
export const TOOLTIP_WORD_LIMIT = 25;

export const firstSentence = (s) => {
  const m = String(s || "").match(/^[^.]+\./);
  return m ? m[0] : String(s || "").slice(0, 110) + "…";
};

/**
 * The one-line state summary on a closed drawer.
 *
 * 🔴 GENERATED FROM LIVE VALUES, NEVER HAND-WRITTEN. A closed drawer is only
 * safe to leave closed because this line is true. A hand-written summary that
 * nobody updates when a thirty-eighth flag lands misreports the section, which
 * is strictly worse than no summary at all, because the operator trusts it
 * enough not to open the drawer.
 */
export const summarise = (section, flags, extra = {}) => {
  const names = [
    ...(section.flags || []),
    ...(section.subGroups || []).flatMap((g) => g.flags),
  ];
  const on = names.filter((n) => flags[n] === true).length;
  const bits = [];

  if (section.id === "doors" && extra.pockets) bits.push(extra.pockets);
  if (section.id === "introductions" && extra.newMemberMinimum) bits.push(extra.newMemberMinimum);
  if (section.id === "chat" && extra.ladder) bits.push(extra.ladder);
  if (section.id === "app" && extra.minVersion) bits.push(`min supported ${extra.minVersion}`);
  // From the card's live answer (eventbriteUtils.integrationsSummary).
  if (section.id === "integrations") return extra.integrations || "Eventbrite: status unknown";
  if (section.id === "engine") {
    bits.push(`refresh ${extra.refreshInterval || "?"}s`);
    bits.push(Number(extra.testerDealMinutes) > 0 ? `testing clock ${extra.testerDealMinutes}m` : "testing clock off");
    return bits.join(" · ");
  }

  // Named exceptions: the things an operator must be able to read WITHOUT
  // opening the drawer. Each names its own alarming state: the truth check
  // alarms when OFF (it used to read "Promise LIVE" while simply ON).
  const exceptions = names.map((n) => summaryAlertFor(n, flags)).filter(Boolean);

  if (names.length) {
    bits.push(...exceptions);
    bits.push(`${on} of ${names.length} on`);
  }
  return bits.join(" · ") || "nothing set";
};

// The new-member minimum's cutoff (2026-10-08) is a moment on the server and
// is typed here in Eastern time, Ottawa's clock, whatever clock this computer
// keeps. Pure, both ways.
const ET = "America/Toronto";
const etParts = (ms) => {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: ET,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .formatToParts(new Date(ms))
      .map((x) => [x.type, x.value])
  );
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute };
};

/** "2026-10-08T18:00" (Eastern) for a stored moment, or "" for none. */
export const etInputOf = (value) => {
  if (!value) return "";
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return "";
  const p = etParts(ms);
  const two = (n) => String(n).padStart(2, "0");
  return `${p.y}-${two(p.mo)}-${two(p.d)}T${two(p.h)}:${two(p.mi)}`;
};

/** The moment an Eastern "2026-10-08T18:00" names, as ISO, or null. */
export const instantOfEtInput = (text) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(text || ""));
  if (!m) return null;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  // Eastern minus UTC at a moment; guessed, then corrected at the guess, so
  // the day the clocks change lands on the right hour.
  const offset = (ms) => {
    const p = etParts(ms);
    return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi) - Math.floor(ms / 60000) * 60000;
  };
  let at = wall - offset(wall);
  at = wall - offset(at);
  return Number.isFinite(at) ? new Date(at).toISOString() : null;
};
